import { Field, StepHeadLarge } from '@/public/features/org/ui/orgNew/parts'
import { useCallback, useState } from 'react'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import type { OrgNewDraft } from '@/public/features/org/types'
import { KakaoPointMap } from '@/shared/ui/KakaoPointMap'
import {
  addressMetaText,
  useKakaoAddressSearch,
  type AddressCandidate,
} from '@/public/features/problem/composables/useKakaoAddressSearch'

export function StepInfo({ draft }: { draft: OrgNewDraft }) {
  return (
    <section className="flex flex-col gap-8">
      <StepHeadLarge
        title="기업 정보를 알려주세요."
        desc="기업 확인에 필요한 기본 정보를 입력해 주세요."
      />
      <div className="flex flex-col gap-6">
        <NameRow draft={draft} />
        <AddressRow draft={draft} />
        <Field
          label="사업자등록번호"
          placeholder="000-00-00000"
          value={draft.bizRegNo}
          onChange={draft.setBizRegNo}
          inputMode="numeric"
        />
        <Field
          label="대표전화"
          placeholder="02-0000-0000"
          value={draft.mainPhone}
          onChange={draft.setMainPhone}
          type="tel"
          inputMode="tel"
        />
        <Field
          label="홈페이지"
          placeholder="https://example.com"
          value={draft.homepage}
          onChange={draft.setHomepage}
          type="url"
          inputMode="text"
          autoComplete="url"
        />
      </div>
    </section>
  )
}
function NameRow({ draft }: { draft: OrgNewDraft }) {
  const hasName = draft.name.trim().length > 0

  return (
    <div className="flex items-start gap-2 [&>label]:min-w-0 [&>label]:flex-1 [&>label]:shrink-0 [&>label]:grow [&>label]:basis-0">
      <Field
        label="기업(단체)명"
        required
        placeholder="예: 해봄 협동조합"
        value={draft.name}
        onChange={draft.setName}
        error={
          draft.nameCheck === 'taken'
            ? '이미 사용 중인 기업(단체)명입니다.'
            : hasName && draft.nameCheck === 'idle'
              ? '기업(단체)명 중복 확인이 필요합니다.'
              : null
        }
        success={draft.nameCheck === 'ok' ? '사용할 수 있는 기업(단체)명입니다.' : null}
      />
      <div className="w-[76px] shrink-0 pt-5">
        <button
          type="button"
          className="ui-pressable inline-flex cursor-pointer items-center justify-center border border-white-700 font-base whitespace-nowrap h-10 w-full rounded-[10px] bg-white-200 px-3 py-2.5 text-body-sm text-black-600 disabled:cursor-not-allowed disabled:border-white-300 disabled:text-white-500"
          onClick={draft.checkName}
          disabled={!hasName || draft.nameCheck === 'checking'}
        >
          중복 확인
        </button>
      </div>
    </div>
  )
}
function AddressRow({ draft }: { draft: OrgNewDraft }) {
  const search = useKakaoAddressSearch()
  const { address } = draft
  const [query, setQuery] = useState(address.text)
  const hasPickedAddress =
    address.lat !== null && address.lng !== null && address.regionCode !== ''

  const pick = useCallback(
    async (c: AddressCandidate) => {
      const resolved = await search.resolve(c)
      draft.setAddress({
        text: resolved.text,
        detail: address.detail,
        regionCode: resolved.regionCode,
        legalCode: resolved.legalCode,
        regionName: resolved.regionName,
        legalName: resolved.legalName,
        // 우편번호는 없을 수 있다 — 없으면 빈 문자열이고 본문에서 키가 빠진다.
        postalCode: resolved.postalCode ?? '',
        // 좌표는 지도에만 쓴다 — 본문에 싣지 않는다(`orgNewDraft` 의 `submit` 참조).
        lat: resolved.lat,
        lng: resolved.lng,
      })
      setQuery(resolved.text)
      search.clear()
    },
    [address.detail, draft, search],
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-2 [&>label]:min-w-0 [&>label]:flex-1 [&>label]:shrink-0 [&>label]:grow [&>label]:basis-0">
        <Field
          label="소재지 주소"
          required
          placeholder="주소를 입력해 주세요."
          value={query}
          onChange={(v) => {
            setQuery(v)
            // 글자를 고치면 앞서 고른 코드를 버린다 — 코드와 글자가 다른 곳을 가리키면 안 된다.
            if (address.regionCode !== '') {
              draft.setAddress({
                text: '',
                detail: address.detail,
                regionCode: '',
                legalCode: '',
                regionName: '',
                legalName: '',
                postalCode: '',
                lat: null,
                lng: null,
              })
            }
          }}
          onEnter={() => void search.search(query)}
        />
        <div className="w-[76px] shrink-0 pt-5">
          <button
            type="button"
            className="ui-pressable inline-flex cursor-pointer items-center justify-center border border-white-700 font-base whitespace-nowrap h-10 w-full rounded-[10px] bg-white-200 px-3 py-2.5 text-body-sm text-black-600 disabled:cursor-not-allowed disabled:border-white-300 disabled:text-white-500"
            onClick={() => void search.search(query)}
            disabled={search.isLoading}
          >
            주소 찾기
          </button>
        </div>
      </div>

      {hasPickedAddress && (
        <Field
          label="상세 주소"
          placeholder=""
          value={address.detail}
          onChange={(v) =>
            draft.setAddress({
              text: address.text,
              detail: v,
              regionCode: address.regionCode,
              legalCode: address.legalCode,
              regionName: address.regionName,
              legalName: address.legalName,
              postalCode: address.postalCode,
              lat: address.lat,
              lng: address.lng,
            })
          }
        />
      )}

      {search.isLoading && (
        <p className="m-0 flex items-center gap-1 text-label font-medium [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 text-violet-600" role="status">
          <InfoIcon />
          <span>주소를 찾는 중입니다.</span>
        </p>
      )}

      {search.error !== null && (
        <p className="m-0 flex items-center gap-1 text-label font-medium [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 text-[#f54550]" role="alert">
          <InfoIcon />
          <span>{search.error}</span>
        </p>
      )}

      {search.results.length > 0 && (
        <ul className="mt-2 mb-0 flex max-h-[232px] list-none flex-col overflow-y-auto rounded-[10px] border border-white-400 p-0 [&>li:first-child>button]:border-t-0" aria-label="주소 검색 결과">
          {search.results.map((c) => (
            <li key={c.id ?? `${c.jibun}-${c.text}`}>
              <button
                type="button"
                className="ui-pressable flex w-full cursor-pointer flex-col gap-0.5 border-0 border-t border-t-white-200 bg-none px-3 py-2.5 text-left"
                onClick={() => void pick(c)}
              >
                {c.placeName !== undefined && (
                  <span className="text-body-sm font-medium text-black-600">{c.placeName}</span>
                )}
                <span
                  className={
                    c.placeName === undefined
                      ? 'text-body-sm font-medium text-black-600'
                      : 'text-label text-black-100'
                  }
                >
                  {c.text}
                </span>
                {c.jibun !== c.text && <span className="text-label text-black-100">{c.jibun}</span>}
                {addressMetaText(c.regionName, c.legalName, c.postalCode) !== '' && (
                  <span className="text-label text-black-100">
                    {addressMetaText(c.regionName, c.legalName, c.postalCode)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {search.hasSearched &&
        !search.isLoading &&
        search.error === null &&
        search.results.length === 0 && (
          <p className="m-0 flex items-center gap-1 text-label font-medium [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 text-[#f54550]" role="alert">
            <InfoIcon />
            <span>찾는 주소가 없습니다. 동 이름이나 도로명으로 다시 찾아주세요.</span>
          </p>
        )}

      {hasPickedAddress && address.lat !== null && address.lng !== null && (
        <KakaoPointMap lat={address.lat} lng={address.lng} />
      )}

      {address.regionCode !== '' && (
        <>
          <p className="m-0 flex items-center gap-1 text-label font-medium [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 text-violet-600">
            <InfoIcon />
            <span>{address.regionName} 으로 등록됩니다.</span>
          </p>
          <p className="m-0 pl-5 text-label text-black-100">
            {addressMetaText(address.regionName, address.legalName, address.postalCode)}
          </p>
        </>
      )}
    </div>
  )
}
